import React, { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import FavoriteEntryButton from 'components/favorite-entry-button';
import { IRewardFragment } from 'generated/graphql-gateway';
import { useCartItemEditingTools } from 'hooks/use-cart-item-editing-tools';
import { useComposeDescription } from 'hooks/use-compose-description';
import useFeatureMenuAddOn from 'hooks/use-feature-menu-add-on';
import { useIsEditCartEnabled } from 'hooks/use-is-edit-cart-enabled';
import { useMapUIPatternToItem } from 'hooks/use-map-ui-pattern-to-cart-entry';
import { useAddOnsItemContainer } from 'pages/cart/addons-item-container/hook/use-addons-item-container';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyContext } from 'state/loyalty';
import { IParsedLoyaltyReward, IRewardAction } from 'state/loyalty/hooks/types';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';

import CartItemButton from './cart-item-button';
import CartItemContainer from './cart-item-container';
import CartItemEditContainer from './cart-item-edit-container';
import CartItemFavorite from './cart-item-favorite';
import CartItemInfoContainer from './cart-item-info-container';
import CartItemName from './cart-item-name';
import { CartItemOffer } from './cart-item-offer';
import CartItemPicture from './cart-item-picture';
import CartItemPrice from './cart-item-price';
import CartItemQuantity from './cart-item-quantity';
import Counter from './cart-item-quantity/counter';
import { PriceAndQuantity } from './price-and-quantity';
import { RedeemReward } from './redeem-reward';
import {
  CartItemDescription,
  CartItemEditButtonContainer,
  Item,
  ItemFreeQuantityDescription,
} from './styled';
import { ICartItemProps } from './types';

const CartItem: React.FC<ICartItemProps> = ({
  item: rawItem,
  selectedOfferId,
  readOnly = false,
  rewardApplied = false,
  showReadOnlyQuantity = false,
}) => {
  const { isCatering } = useServiceModeContext();
  const hasSelectedOffer = Boolean(selectedOfferId);
  const isRewardDiscount = Boolean((rawItem as unknown as IRewardAction).rewardBenefitId);
  const { formatMessage } = useIntl();
  const isEditCartEnabled = useIsEditCartEnabled({
    cartHasIncentive: hasSelectedOffer || rewardApplied,
  });
  const { sections: addOnSections } = useFeatureMenuAddOn();
  const { cartEntries } = useOrderContext();
  const { countMaximumFreeOffer } = useAddOnsItemContainer();

  const enableFreeAddonOnCartFeatureFlag = useFlag(LaunchDarklyFlag.ENABLE_FREE_ADDON_ON_CART);

  // Needed to properly useComposeDescription
  const { item, loading } = useMapUIPatternToItem(rawItem);

  const { handleEditItem, handleRemoveItem, handleQuantityChange } = useCartItemEditingTools({
    item,
    isOffer: hasSelectedOffer,
  });

  const description = useComposeDescription(item, {
    includeModifiers: true,
    returnTypeArray: true,
    includeQuantity: showReadOnlyQuantity,
  });
  const hasImage = !!item?.image?.asset;
  const itemKey = useMemo(() => item.cartId || item._id, [item._id, item.cartId]);
  const itemQuantity = item?.quantity;
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const dispatch = useAppDispatch();
  const loyaltyEnabled = useIsLoyaltyEnabled();
  const { getAvailableRewardFromCartEntry } = useLoyaltyContext();
  const { isCartEntryInSwaps } = useOrderContext();
  const loyaltyRewardAvailable = getAvailableRewardFromCartEntry(item);
  const favoriteEnabled = useFlag(LaunchDarklyFlag.ENABLE_FAVORITES);

  const handleRemoveReward = useCallback(() => {
    dispatch(
      actions.loyalty.removeAppliedReward({
        rewardBenefitId: (rawItem as unknown as IParsedLoyaltyReward).rewardId,
        cartId: 'discount-offer',
      })
    );
  }, [dispatch, rawItem]);

  if (item.isDonation) {
    return null;
  }

  const isSwap = isCartEntryInSwaps(item);
  const loyaltyOffer = appliedOffers.find(
    lylOffer => lylOffer.cartId === item.cartId || lylOffer.swap?.cartId === item.cartId
  );
  const isExtra = item?.isExtra;
  const isItemEditableInline =
    !hasSelectedOffer && !isSwap && !isExtra && !loyaltyOffer && !isRewardDiscount;
  const hasOffer = selectedOfferId === item.sanityId || loyaltyOffer;
  const showFavorite = favoriteEnabled && !hasOffer && !isSwap;

  const renderItemFreeQuantityDescription = (): JSX.Element | null => {
    const optionsAcceptingFreeOffers = addOnSections.flatMap(
      ({ options, enableAddonAsFreeItem }) => {
        if (!enableAddonAsFreeItem) {
          return [];
        }

        const { itemsAcceptingFreeOffers } =
          options.find(({ freeOption }) => freeOption?._id === item._id) ?? {};

        return itemsAcceptingFreeOffers ?? [];
      }
    );

    const isFreeItem = item.price === 0 || item.price === undefined;

    const freeOffers = optionsAcceptingFreeOffers.length ? optionsAcceptingFreeOffers : undefined;

    const maxQtdFree = countMaximumFreeOffer(cartEntries, freeOffers);

    return isExtra && isFreeItem && maxQtdFree > 0 ? (
      <ItemFreeQuantityDescription>
        {formatMessage({ id: 'youCanAddUpTo' })}
        <strong>{` ${maxQtdFree} ${item.name}`}</strong>. {formatMessage({ id: 'itsFree' })}
      </ItemFreeQuantityDescription>
    ) : null;
  };

  return (
    <Item isCatering={isCatering} data-testid="cart-item">
      {loyaltyOffer?.id && (
        <CartItemOffer loyaltyOfferId={loyaltyOffer.id} cmsOfferId={loyaltyOffer.cmsId} />
      )}
      {item.image && hasImage && (
        <CartItemPicture isCatering={isCatering} image={item.image} name={item.name} />
      )}
      <CartItemContainer $hasOffer={!!loyaltyOffer}>
        <CartItemInfoContainer
          tabIndex={0}
          aria-labelledby={`cart-section-heading-label item-quantity-${itemQuantity} cart-item-${itemKey} item-price-${itemKey} cart-price-${itemKey}`}
        >
          <div id={`cart-item-${itemKey}`}>
            <CartItemName data-testid="cart-item-label" id="cart-item-label">
              {item.name}
            </CartItemName>
            {!loading && enableFreeAddonOnCartFeatureFlag && renderItemFreeQuantityDescription()}
            <CartItemDescription data-testid="cart-item-description">
              {!loading &&
                description.map((descriptionItem, index) => (
                  <span key={`descriptionItem-${index}`}>
                    {descriptionItem}
                    <br />
                  </span>
                ))}
            </CartItemDescription>
          </div>
          {showFavorite && (
            <CartItemFavorite>
              <FavoriteEntryButton cartEntry={item} />
            </CartItemFavorite>
          )}
          {!favoriteEnabled && (
            <>
              {isRewardDiscount ? (
                formatMessage({ id: 'free' })
              ) : (
                <PriceAndQuantity
                  showQuantity={readOnly && isCatering}
                  item={item}
                  isOffer={hasSelectedOffer}
                  rewardApplied={rewardApplied}
                />
              )}
            </>
          )}
        </CartItemInfoContainer>
        {loyaltyEnabled && loyaltyRewardAvailable && isItemEditableInline && (
          <RedeemReward
            isRewardApplied={rewardApplied}
            item={item}
            reward={loyaltyRewardAvailable}
          />
        )}
        {loyaltyEnabled && isRewardDiscount && (
          <RedeemReward
            isRewardApplied={rewardApplied}
            item={item}
            reward={rawItem as unknown as IRewardFragment}
            isRewardDiscount
          />
        )}
        {favoriteEnabled && (
          <CartItemPrice>
            <CartItemName data-testid="cart-price-desc" id="cart-price-desc">
              {formatMessage({ id: 'itemTotal' })}
            </CartItemName>
            {isRewardDiscount ? (
              formatMessage({ id: 'free' })
            ) : (
              <PriceAndQuantity
                showQuantity={readOnly && isCatering}
                item={item}
                isOffer={hasSelectedOffer}
                rewardApplied={rewardApplied}
              />
            )}
          </CartItemPrice>
        )}
        {!readOnly && (
          <CartItemEditContainer isCatering={isCatering}>
            <CartItemEditButtonContainer>
              {isEditCartEnabled && !loyaltyOffer && !isRewardDiscount && !isSwap && (
                <CartItemButton
                  aria-label={`${formatMessage({ id: 'edit' })} ${item.name} `}
                  data-testid="edit-button"
                  onClick={handleEditItem}
                >
                  {formatMessage({ id: 'edit' })}
                </CartItemButton>
              )}
              <CartItemButton
                aria-label={`${formatMessage({ id: 'remove' })} ${item.name} `}
                data-testid="remove-button"
                onClick={isRewardDiscount ? handleRemoveReward : handleRemoveItem}
              >
                {formatMessage({ id: 'remove' })}
              </CartItemButton>
            </CartItemEditButtonContainer>

            <CartItemEditButtonContainer>
              {isItemEditableInline && (
                <CartItemQuantity item={item} updateQuantity={handleQuantityChange} />
              )}
              {isExtra && <Counter>{`${item.quantity}x`}</Counter>}
            </CartItemEditButtonContainer>
          </CartItemEditContainer>
        )}
      </CartItemContainer>
    </Item>
  );
};

export default React.memo(CartItem);
